import { useState, useEffect, useMemo, ReactNode } from 'react';
import { isEqual } from 'lodash-es';
import { contextFactory } from '../../hooks';

interface SlideCarouselContextProps {
  activeId: string;
  setActiveId: (id: string) => void;
  slideIds: string[];
  canDrag?: boolean;
  slideGap: number;
}

export const [SlideCarouselContext, useSlideCarousel] = contextFactory<SlideCarouselContextProps>();

interface SlideCarouselProviderProps {
  children: ReactNode;
  slides: string[];
  initialSlide?: string;
  canDrag?: boolean;
  slideGap?: number;
}

export const SlideCarouselProvider = ({
  children,
  slides,
  initialSlide,
  canDrag,
  slideGap = 0,
}: SlideCarouselProviderProps) => {
  const [activeId, setActiveId] = useState<string>(initialSlide ?? slides?.[0] ?? '');
  const [slideIds, setSlideIds] = useState<string[]>(slides);

  useEffect(() => {
    if (!isEqual(slides, slideIds)) {
      setSlideIds(slides);
    }
  }, [slides, slideIds]);

  useEffect(() => {
    if (slideIds.length > 0 && !slideIds.includes(activeId)) {
      slideIds[0] && setActiveId(slideIds[0]);
    }
  }, [slideIds, activeId]);

  const value = useMemo(
    () => ({ activeId, setActiveId, slideIds, canDrag, slideGap }),
    [activeId, slideIds, canDrag, slideGap]
  );

  return <SlideCarouselContext.Provider value={value}>{children}</SlideCarouselContext.Provider>;
};
